import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { ApiService } from '../services/api';
import { UserSettings, ViewOptions, TooltipField, UserSettingsState, TriangulationSettings } from '../types/userSettings';

// Helper function to create ApiService with current token  
const createApiService = (getState: () => any) => {
    const state = getState();
    const apiService = new ApiService(
        process.env.REACT_APP_API_URL || 'http://localhost:3051',
        process.env.REACT_APP_API_URL || 'http://localhost:3051/api'
    );
    // Use token from Redux state, or fallback to localStorage if state hasn't been hydrated yet
    const token = state.auth?.token || localStorage.getItem('token');
    if (token) {
        apiService.setToken(token);
    }
    return apiService;
};


// Default settings
const getDefaultSettings = (language: string): UserSettings => ({
    userId: '',
    viewOptions: {
        showDeviceNames: true,
        showGatewayCoverage: false,
        showGridLines: false,
        showRSSIValues: false,
        showLastSeen: false,
        showConfidence: false,
        autoZoom: false,
        showDeviceAsNameBox: false,
    },
    tooltipFields: [
        {
            id: 'name',
            label: language === 'he' ? 'שם התקן' : 'Device Name',
            enabled: true,
            order: 1,
        },
        {
            id: 'mac',
            label: language === 'he' ? 'כתובת MAC' : 'MAC Address',
            enabled: false,
            order: 2,
        },
        {
            id: 'rssi',
            label: language === 'he' ? 'עוצמת אות' : 'RSSI Value',
            enabled: true,
            order: 3,
        },
        {
            id: 'lastSeen',
            label: language === 'he' ? 'נראה לאחרונה' : 'Last Seen',
            enabled: true,
            order: 4,
        },
        {
            id: 'gateway',
            label: language === 'he' ? 'שער חזק ביותר' : 'Strongest Gateway',
            enabled: false,
            order: 5,
        },
        {
            id: 'status',
            label: language === 'he' ? 'סטטוס חיבור' : 'Connection Status',
            enabled: true,
            order: 6,
        },
    ],
    triangulation: {
        enabled: true,
        algorithm: 'auto',
        minSignalCount: 2,
        minConfidence: 0.3,
        signalAgeWeight: 0.8,
        rssiWeight: 1.0,
        showDebugInfo: false,
    },
});

const initialState: UserSettingsState = {
    settings: null,
    loading: false,
    error: null,
};

// Async thunks
export const fetchUserSettings = createAsyncThunk(
    'userSettings/fetchUserSettings',
    async (_, { getState }) => {
        const apiService = createApiService(getState);
        const state = getState() as any;
        const language = state.ui?.language || 'en';
        return { settings: await apiService.fetchUserSettings(), language };
    }
);

export const updateViewOption = createAsyncThunk(
    'userSettings/updateViewOption',
    async ({ key, value }: { key: keyof ViewOptions; value: boolean }, { getState }) => {
        const apiService = createApiService(getState);
        return await apiService.updateViewOption(key, value);
    }
);

export const updateTooltipFields = createAsyncThunk(
    'userSettings/updateTooltipFields',
    async (tooltipFields: TooltipField[], { getState }) => {
        const apiService = createApiService(getState);
        return await apiService.updateTooltipFields(tooltipFields);
    }
);

export const updateTriangulationSettings = createAsyncThunk(
    'userSettings/updateTriangulationSettings',
    async (triangulation: TriangulationSettings, { getState }) => {
        const apiService = createApiService(getState);
        return await apiService.updateUserSettings({ triangulation });
    }
);

export const updateUserSettings = createAsyncThunk(
    'userSettings/updateUserSettings',
    async (settings: { viewOptions?: ViewOptions; tooltipFields?: TooltipField[]; triangulation?: TriangulationSettings }, { getState }) => {
        const apiService = createApiService(getState);
        return await apiService.updateUserSettings(settings);
    }
);

const userSettingsSlice = createSlice({
    name: 'userSettings',
    initialState,
    reducers: {
        // Local actions for immediate UI updates (will be synced to server)
        setViewOptionLocal: (state, action: PayloadAction<{ key: keyof ViewOptions; value: boolean }>) => {
            if (state.settings) {
                state.settings.viewOptions[action.payload.key] = action.payload.value;
            }
        },
        setTooltipFieldsLocal: (state, action: PayloadAction<TooltipField[]>) => {
            if (state.settings) {
                state.settings.tooltipFields = action.payload;
            }
        },
        setTriangulationSettingsLocal: (state, action: PayloadAction<TriangulationSettings>) => {
            if (state.settings) {
                state.settings.triangulation = action.payload;
            }
        },
        initializeDefaultSettings: (state, action: PayloadAction<string>) => {
            if (!state.settings) {
                state.settings = getDefaultSettings(action.payload);
            }
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch user settings
            .addCase(fetchUserSettings.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchUserSettings.fulfilled, (state, action) => {
                state.loading = false;
                const { settings, language } = action.payload;
                // Merge fetched settings with defaults to ensure all fields are present
                const defaultSettings = getDefaultSettings(language);
                state.settings = {
                    ...defaultSettings,
                    ...settings,
                    viewOptions: {
                        ...defaultSettings.viewOptions,
                        ...settings.viewOptions
                    },
                    tooltipFields: settings.tooltipFields || defaultSettings.tooltipFields,
                    triangulation: {
                        ...defaultSettings.triangulation,
                        ...settings.triangulation
                    }
                };
            })
            .addCase(fetchUserSettings.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to fetch user settings';
            })
            // Update view option
            .addCase(updateViewOption.fulfilled, (state, action) => {
                state.settings = action.payload;
            })
            .addCase(updateViewOption.rejected, (state, action) => {
                state.error = action.error.message || 'Failed to update view option';
            })
            // Update tooltip fields
            .addCase(updateTooltipFields.fulfilled, (state, action) => {
                state.settings = action.payload;
            })
            .addCase(updateTooltipFields.rejected, (state, action) => {
                state.error = action.error.message || 'Failed to update tooltip fields';
            })
            // Update triangulation settings
            .addCase(updateTriangulationSettings.fulfilled, (state, action) => {
                state.settings = action.payload;
            })
            .addCase(updateTriangulationSettings.rejected, (state, action) => {
                state.error = action.error.message || 'Failed to update triangulation settings';
            })
            // Update user settings
            .addCase(updateUserSettings.fulfilled, (state, action) => {
                state.settings = action.payload;
            })
            .addCase(updateUserSettings.rejected, (state, action) => {
                state.error = action.error.message || 'Failed to update user settings';
            });
    },
});

export const { setViewOptionLocal, setTooltipFieldsLocal, setTriangulationSettingsLocal, initializeDefaultSettings } = userSettingsSlice.actions;

// Selectors
export const selectUserSettings = (state: any) => state.userSettings.settings;
export const selectViewOptions = (state: any) => state.userSettings.settings?.viewOptions;
export const selectTooltipFields = (state: any) => state.userSettings.settings?.tooltipFields;
export const selectTriangulationSettings = (state: any) => state.userSettings.settings?.triangulation;
export const selectUserSettingsLoading = (state: any) => state.userSettings.loading;
export const selectUserSettingsError = (state: any) => state.userSettings.error;

export default userSettingsSlice.reducer;
